import React, { useState } from 'react';
import ThumbIcon from 'shared/components/ui/ThumbIcon';
import { submitNodeFeedback, retractNodeFeedback } from 'features/evals3/api/evals3Api';
import StepAnswer, { specOf, shouldText, complete } from './StepAnswer';

/**
 * The label under ONE step of a verdict's path: Right, or what it should have
 * said, typed to the node (StepAnswer). One label per person per step;
 * re-saving updates. Travels with dataset rows as evaluations[].node_labels.
 */
export default function StepFeedback({ teamId, run, step, node, value = null, onChange, highlight = false }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  if (!teamId || !run?.id || !step?.node_id) return null;

  const spec = specOf(node);
  const said = step.output?.value ?? step.value ?? null;
  const labelled = Boolean(value);
  const agree = value?.agree === true;
  const stop = (e) => { e.stopPropagation(); e.preventDefault(); };

  const openForm = (e) => {
    stop(e);
    setDraft(value ? { agree: value.agree === true, should_have_been: value.should_have_been || {} } : null);
    setReason(value?.reason || '');
    setError(null);
    setOpen((o) => !o);
  };

  const save = async (e) => {
    stop(e);
    if (!complete(spec, draft)) { setError(draft ? 'Fill in every field.' : 'Choose Right or Should have been.'); return; }
    setBusy(true); setError(null);
    try {
      const body = draft.agree
        ? { eval_run_id: run.id, node_id: step.node_id, agree: true, reason: reason.trim() || null }
        : { eval_run_id: run.id, node_id: step.node_id, should_have_been: spec ? Object.fromEntries(Object.keys(spec).map((k) => [k, draft.should_have_been[k]])) : { text: draft.should_have_been.text || '' }, reason: reason.trim() || null };
      const saved = await submitNodeFeedback(teamId, body);
      onChange?.(saved);
      setOpen(false);
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Could not save');
    } finally { setBusy(false); }
  };

  const retract = async (e) => {
    stop(e);
    if (!value?.id) return;
    setBusy(true);
    try { await retractNodeFeedback(teamId, value.id); onChange?.(null); setOpen(false); }
    catch (err) { setError(err?.response?.data?.message || err.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="relative mt-1 inline-flex flex-col items-start" onClick={stop} role="presentation" data-testid={`step-feedback-${step.node_id}`}>
      <button type="button" onClick={openForm} disabled={busy} aria-pressed={labelled} aria-expanded={open}
        aria-label={labelled ? `Edit the label on ${node?.name || step.node_id}` : `Label ${node?.name || step.node_id}`}
        title={labelled ? 'Click to edit' : 'Right, or what it should have said'}
        className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] transition-colors ${labelled ? 'bg-brand-soft text-[color:var(--text-primary)]' : highlight ? 'border border-[color:var(--feedback-warning)] text-[color:var(--text-secondary)]' : 'text-[color:var(--text-muted)] hover:bg-surface-hover hover:text-[color:var(--text-primary)]'}`}>
        <ThumbIcon direction={agree ? 'up' : 'down'} filled={labelled} size={12} />
        {labelled
          ? <span>{agree ? 'Right' : `Should have been ${shouldText(spec, value)}`}</span>
          : <span>{highlight ? 'Label this step' : 'Label…'}</span>}
      </button>
      {open && (
        <div className="absolute left-0 top-full z-20 mt-1 w-80 space-y-2 rounded-md border border-[color:var(--border-default)] bg-surface p-2 text-[11px] shadow-lg" onClick={stop} role="group" aria-label={`Label ${node?.name || step.node_id}`}>
          <StepAnswer node={node} said={said} value={draft} onChange={setDraft} />
          <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} maxLength={4000} aria-label="Why"
            placeholder="Why? Optional" className="w-full rounded border border-[color:var(--border-default)] bg-[color:var(--surface-sunken)] p-1.5 text-[11px] text-[color:var(--text-primary)] outline-none" />
          {error && <div className="text-[color:var(--feedback-error)]">{error}</div>}
          <div className="flex items-center gap-1">
            <button type="button" disabled={busy} onClick={save} className="rounded bg-brand px-2 py-1 font-medium text-ink-onBrand hover:bg-brand-hover disabled:opacity-40">{busy ? 'Saving…' : 'Save'}</button>
            <button type="button" onClick={(e) => { stop(e); setOpen(false); }} className="rounded px-2 py-1 text-[color:var(--text-muted)] hover:bg-surface-hover">Cancel</button>
            {value?.id && <button type="button" disabled={busy} onClick={retract} className="ml-auto rounded px-2 py-1 text-[color:var(--text-muted)] hover:bg-surface-hover">Remove</button>}
          </div>
        </div>
      )}
    </div>
  );
}
